// src/components/strategies/TradesTable.jsx

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineShare } from 'react-icons/hi';
import Pagination from '../shared/Pagination';
import TradeDetailModal from './TradeDetailModal';
import TradeShareModal from './TradeShareModal';

const normalizeTime = (ts) => {
    if (!ts) return null;
    if (typeof ts === 'string' && ts.length > 13) return parseInt(ts.slice(0, 13), 10);
    if (typeof ts === 'number') return ts;
    return new Date(ts).getTime();
};

const formatDate = (timestamp, lang) => {
  const ms = normalizeTime(timestamp);
  if (!ms) return 'N/A';
  return new Date(ms).toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).replace(',', '');
};

const formatDuration = (ms, t) => {
    if (isNaN(ms) || ms < 0) return 'N/A';
    let totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds >= 0) parts.push(`${seconds}s`);
    return parts.join(' ');
};

const PositionCell = ({ direction, symbol }) => {
  if (!direction || !symbol) return <span>-</span>;
  const isLong = String(direction).toUpperCase() === 'LONG';
  return (
    <span className={`font-semibold ${isLong ? 'text-green-400' : 'text-red-400'}`}>
      {`${symbol} ${String(direction).toUpperCase()}`}
    </span>
  );
};

const TradesTable = ({ title, trades, type, strategyParams, strategyName }) => {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const tradesPerPage = 30;
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [shareTrade, setShareTrade] = useState(null);

  const processedTrades = useMemo(() => {
    if (!trades) return [];
    if (Array.isArray(trades)) {
        if (type === 'completed') return [...trades].reverse();
        return trades;
    }
    if (typeof trades === 'object') return [trades];
    return [];
  }, [trades, type]);

  if (!processedTrades || processedTrades.length === 0) {
    return (
      <div className="bg-slate-800 p-6 rounded-xl shadow-lg mt-6 text-white text-center">
        <h3 className="text-xl font-semibold mb-4">{title}</h3>
        <p className="text-gray-400">{t('trades.empty')}</p>
      </div>
    );
  }

  const totalPages = Math.ceil(processedTrades.length / tradesPerPage);
  const indexOfFirstTrade = (currentPage - 1) * tradesPerPage;
  const currentTrades = processedTrades.slice(indexOfFirstTrade, indexOfFirstTrade + tradesPerPage);
  
  // Явный список заголовков, чтобы избежать разваливания на буквы
  const currentHeaders = type === 'open' 
    ? [
        t('trades.headers.open.num'),
        t('trades.headers.open.time'),
        t('trades.headers.open.zscore'),
        t('trades.headers.open.level'),
        t('trades.headers.open.pos_a'),
        t('trades.headers.open.price_a'),
        t('trades.headers.open.pos_b'),
        t('trades.headers.open.price_b'),
      ]
    : [
        t('trades.headers.completed.num'),
        t('trades.headers.completed.pos_a'),
        t('trades.headers.completed.pos_b'),
        t('trades.headers.completed.entries'),
        t('trades.headers.completed.entry_time'),
        t('trades.headers.completed.exit_time'),
        t('trades.headers.completed.duration'),
        t('trades.headers.completed.close_z'),
        t('trades.headers.completed.pnl'),
      ];

  const { symbol_a, symbol_b } = strategyParams || {};

  return (
    <div className="bg-slate-800 p-6 rounded-xl shadow-lg mt-6">
      <h3 className="text-xl font-semibold text-white mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-300">
          <thead className="text-[10px] text-gray-400 uppercase bg-slate-900/50">
            <tr>
              {currentHeaders.map((header, idx) => (
                <th key={idx} className="px-4 py-3 whitespace-nowrap border-b border-slate-700">{header}</th>
              ))}
              {type === 'completed' && (
                <th className="px-4 py-3 whitespace-nowrap border-b border-slate-700 text-center">
                  {t('trades.headers.completed.share')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentTrades.map((trade, index) => {
              const start = normalizeTime(trade.entry_time || trade.time);
              const end = normalizeTime(trade.exit_time);
              const duration = (start && end) ? end - start : 0;

              return (
              <tr 
                key={index} 
                className={`border-b border-slate-700/50 transition-colors ${type === 'completed' ? 'hover:bg-slate-700/50 cursor-pointer' : 'hover:bg-slate-700/30'}`}
                onClick={() => type === 'completed' && setSelectedTrade(trade)}
              >
                {type === 'completed' ? (
                  <>
                    <td className="px-4 py-3">{processedTrades.length - indexOfFirstTrade - index}</td>
                    <td className="px-4 py-3"><PositionCell direction={trade.pos_a} symbol={symbol_a} /></td>
                    <td className="px-4 py-3"><PositionCell direction={trade.pos_b} symbol={symbol_b} /></td>
                    <td className="px-4 py-3 text-center">{trade.entries?.length || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-[11px]">{formatDate(trade.entry_time, i18n.language)}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-[11px]">{formatDate(trade.exit_time, i18n.language)}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-[11px] text-slate-400">{formatDuration(duration, t)}</td>
                    <td className="px-4 py-3 font-mono">{(trade.exit_z || 0).toFixed(4)}</td>
                    <td className={`px-4 py-3 font-mono font-bold ${parseFloat(trade.net_pnl_usdt) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {(parseFloat(trade.net_pnl_usdt) || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShareTrade(trade);
                        }}
                        className="inline-flex items-center justify-center rounded-lg border border-slate-600/60 bg-slate-900/40 p-2 text-slate-300 hover:border-blue-500/60 hover:text-white transition-colors"
                        title={t('trades.headers.completed.share')}
                      >
                        <HiOutlineShare className="h-4 w-4" />
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3">{indexOfFirstTrade + index + 1}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-[11px]">{formatDate(trade.time, i18n.language)}</td>
                    <td className="px-4 py-3 font-mono">{(parseFloat(trade.z) || 0).toFixed(4)}</td>
                    <td className="px-4 py-3 font-mono">{trade.level}</td>
                    <td className="px-4 py-3"><PositionCell direction={trade.pos_a} symbol={symbol_a} /></td>
                    <td className="px-4 py-3 font-mono">${trade.price_a}</td>
                    <td className="px-4 py-3"><PositionCell direction={trade.pos_b} symbol={symbol_b} /></td>
                    <td className="px-4 py-3 font-mono">${trade.price_b}</td>
                  </>
                )}
              </tr>
            )})}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      )}

      <TradeDetailModal 
        trade={selectedTrade} 
        onClose={() => setSelectedTrade(null)}
        symbol_a={symbol_a}
        symbol_b={symbol_b}
      />

      <TradeShareModal
        trade={shareTrade}
        onClose={() => setShareTrade(null)}
        strategyParams={strategyParams}
        strategyName={strategyName}
      />
    </div>
  );
};

export default TradesTable;
